import { Err, isOk, Result } from "@pacote/result"
import useSWR from "swr"
import useSWRMutation from "swr/mutation"
import { z } from "zod"

import {
  ApiFailure,
  baseApiErrorResponseSchema,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { withAuthClientFetcher } from "./base-fetchers"

/* -------------------------------------------------------------------------------------------------
 * types
 * -----------------------------------------------------------------------------------------------*/
type CheckOtpPayload = {
  otpCode: string
  username: string
  billId: number
  jwt: string
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const checkOtpServiceResponseSchema = baseApiResponseSchema.extend({
  name: z.string(),
  surname: z.string(),
  birthDate: z.string(),
  phoneNumber: z.string(),
  email: z.string(),
  username: z.string(),
  token: z.string(),
})

type CheckOtpResponse = z.infer<typeof checkOtpServiceResponseSchema>

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const checkOtpServiceErrorResponseSchema = baseApiErrorResponseSchema.extend({
  counterCheck: z.number().optional(),
  counterRefresh: z.number().optional(),
})

type CheckOtpErrorResponse = z.infer<typeof checkOtpServiceErrorResponseSchema>

type CheckOtpFetcherReturn = Result<
  CheckOtpResponse,
  { type: "ERROR_MESSAGE"; errorMessage?: string }
>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/
const checkOtpServiceEndpoint = "/otp-security-ms/ms/otp/check"
/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/
const checkOtpFetcher = async (
  key: string,
  { arg }: { arg: CheckOtpPayload },
): Promise<CheckOtpFetcherReturn> => {
  /*--------------------acquire recaptcha token--------------------*/

  const recaptchaToken = await (window as any).grecaptcha.enterprise.execute(
    window.APP_KEY_RE_CAPTCHA,
    {
      action: "PASSWORD_OTP_RESET",
    },
  )

  if (!recaptchaToken)
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `Recaptcha token not found on request: ${key}`,
    })

  /*--------------------make check otp request--------------------*/
  const responseResult = await withAuthClientFetcher<
    CheckOtpResponse,
    CheckOtpErrorResponse
  >({
    url: key,
    method: "POST",
    body: { ...arg, recaptchaToken },
  })

  if (isOk(responseResult)) return responseResult

  const response = responseResult.value

  if (response.status !== 422)
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 httpStatus was returned on request: ${key}`,
    })

  const errorInfos = response.data.error?.errorInfos || {}
  const counterCheck = response.data.counterCheck
  const counterRefresh = response.data.counterRefresh

  const errorKey = Object.keys(errorInfos)[0] as keyof typeof errorInfos

  if (!errorInfos[errorKey] || !errorInfos[errorKey][0])
    throw new UnhandledApiError({
      status: ApiFailure.INVALID_ERROR_RESPONSE_ERROR,
      message: `An invalid error response was returned on request: ${key}`,
    })

  let errorMessage = errorInfos[errorKey][0]

  if (counterCheck && counterCheck <= 2) errorMessage = "Otp.invalidOtpCode"
  if (counterCheck && counterCheck >= 3) {
    errorMessage = "Otp.generateNewCode"
    if (counterRefresh && counterRefresh >= 3)
      errorMessage = "Otp.expiredOtpCode"
  }

  return Err({
    type: "ERROR_MESSAGE",
    errorMessage,
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/
/**E' un po' un barbatrucco*/
const useCheckOtpMutation = () => {
  const { data } = useSWR<CheckOtpFetcherReturn>(checkOtpServiceEndpoint)
  const { data: checkOtpInlineData, ...rest } = useSWRMutation(
    checkOtpServiceEndpoint,
    checkOtpFetcher,
    {
      populateCache: true,
    },
  )

  return { data, checkOtpInlineData, ...rest }
}

export { useCheckOtpMutation }
